import { Col, Row, Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import api from 'api'
import { DefaultLayout } from 'components/layouts/DefaultLayout'
import { NewTitle } from 'components/layouts/Title'
import { EventCard } from 'components/models/events/EventCard'
import { EventCarousel } from 'components/models/events/EventCarousel'
import { EventFilterForm } from 'components/models/events/EventFilterForm'
import { Button } from 'components/shared/Button'
import { NextHead } from 'components/shared/NextHead'
import { EVENT_SEARCH_KEY } from 'constants/search'
import colors from 'helpers/colors'
import { numberWithDelimiter } from 'helpers/numberWithDelimiter'
import { zIndex } from 'helpers/zIndex'
import { useEvents } from 'hooks/events'
import { useLockScrollingBackContent } from 'hooks/shared'
import { Moment } from 'moment'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextPageWithHead } from 'pages/_app'
import FilterIcon from 'public/icons/filter.svg'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { queryStringParams } from 'utils/queryStringParams'

type Props = {
  topEvent: Event | null
}

const Page: NextPageWithHead<Props> = () => {
  const router = useRouter()
  const path = router.asPath.split(/\?/)[0]

  const { xs } = useBreakpoint()
  const [isOpenFilterFormForSp, setIsOpenFilterFormForSp] = useState(false)

  const { lockScrollingBackContent, unlockScrollingBackContent } =
    useLockScrollingBackContent()
  const { t } = useTranslation()

  const [eventSearchText, setEventSearchText] = useState('')
  const [eventSearchTextForPlacesCity, setEventSearchTextForPlacesCity] =
    useState('')
  const [startAt, setStartAt] = useState<Moment>()
  const [endAt, setEndAt] = useState<Moment>()

  const queryObject = useMemo(() => {
    return {
      [EVENT_SEARCH_KEY.keyword]: eventSearchText.replace('　', ' ').split(' '),
      [EVENT_SEARCH_KEY.event_places]: eventSearchTextForPlacesCity
        .replace('　', ' ')
        .split(' '),
      [EVENT_SEARCH_KEY.start_at]: endAt?.toString(),
      [EVENT_SEARCH_KEY.end_at]: startAt?.toString(),
    }
  }, [endAt, startAt, eventSearchText, eventSearchTextForPlacesCity])

  const queryString = useMemo(
    () => queryStringParams(queryObject),
    [queryObject],
  )

  const setValuesCallback = useCallback(() => {
    router.push(
      `${path}/?${decodeURIComponent(queryString?.toString())}`,
      undefined,
      { shallow: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const { data: eventsForTop = [] } = useEvents(
    {
      title_or_owner_name_or_event_places_name_or_event_places_city_or_event_places_state_cont_any:
        [''],
      event_places_state_or_event_places_city_cont_any: [''],
    },
    5,
  )
  const { data: events = [] } = useEvents({
    title_or_owner_name_or_event_places_name_or_event_places_city_or_event_places_state_cont_any:
      eventSearchText.replace('　', ' ').split(' '),
    event_places_state_or_event_places_city_cont_any:
      eventSearchTextForPlacesCity.replace('　', ' ').split(' '),
    start_at_lteq_any: endAt?.toString(),
    end_at_gteq_any: startAt?.toString(),
  })

  const handleClickClearButton = useCallback(() => {
    setEventSearchText('')
    setStartAt(undefined)
    setEndAt(undefined)
  }, [])

  const handleClickFilerButton = useCallback(() => {
    setIsOpenFilterFormForSp(true)
    lockScrollingBackContent()
  }, [lockScrollingBackContent])

  const handleClickCancelButton = useCallback(() => {
    setIsOpenFilterFormForSp(false)
    unlockScrollingBackContent()
  }, [unlockScrollingBackContent])

  const handleClickOkButton = useCallback(() => {
    setValuesCallback()
    setIsOpenFilterFormForSp(false)
    unlockScrollingBackContent()
  }, [unlockScrollingBackContent, setValuesCallback])

  // PCサイズの場合、入力値を即時反映させる
  useEffect(() => {
    if (!xs) setValuesCallback()
  }, [setValuesCallback, xs])

  return (
    <DefaultLayout onlyLogo={false}>
      <div style={{ padding: xs ? '40px 16px 0px' : '40px 64px 0px' }}>
        <NewTitle title1="Events" title2={t('イベント一覧')} />
      </div>
      <div
        style={{
          padding: xs ? '40px 16px 0px' : '60px 64px 0px',
          minHeight: 566,
        }}
      >
        <EventCarousel events={eventsForTop} />
      </div>
      <div
        style={{
          display: 'flex',
          marginTop: xs ? 24 : 40,
          gridGap: '56px',
          padding: xs ? '0px 16px 40px' : '0px 68px 100px',
          minHeight: 566,
        }}
      >
        <StyledArtWorkFilterFormWrapper
          xs={Boolean(xs)}
          isOpenFilterFormForSp={isOpenFilterFormForSp}
        >
          {xs && (
            <FixedWrapper
              style={{
                position: 'sticky',
                top: 0,
                justifyContent: 'space-between',
                borderBottom: `1px solid ${colors.grey}`,
              }}
            >
              <a
                onClick={handleClickCancelButton}
                style={{ textDecorationLine: 'underline' }}
              >
                {t('キャンセル')}
              </a>
              <Button
                size="sm"
                type="secondary"
                onClick={handleClickClearButton}
                style={{ padding: '12px 16px' }}
              >
                {t('条件をクリア')}
              </Button>
            </FixedWrapper>
          )}
          {(!xs || isOpenFilterFormForSp) && (
            <div
              style={{
                padding: xs ? '16px' : '',
                width: xs ? '100%' : '225px',
              }}
            >
              <EventFilterForm
                eventSearchText={eventSearchText}
                setEventSearchText={(value) => setEventSearchText(value)}
                eventSearchTextForPlacesCity={eventSearchTextForPlacesCity}
                setEventSearchTextForPlacesCity={(value) =>
                  setEventSearchTextForPlacesCity(value)
                }
                onClickOkButton={handleClickOkButton}
              />
            </div>
          )}
        </StyledArtWorkFilterFormWrapper>
        <div style={{ flex: 1 }}>
          {xs && (
            <div
              style={{
                position: 'sticky',
                top: '60px',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                background: 'white',
                padding: '12px 0',
              }}
            >
              <Button
                style={{
                  display: 'flex',
                  fontSize: '14px',
                  fontWeight: 400,
                  gap: '4px',
                }}
                size="md"
                type="primary"
                onClick={handleClickFilerButton}
              >
                <FilterIcon />
                {t('フィルター')}
              </Button>
            </div>
          )}
          <div style={{ height: 48, display: 'flex', alignItems: 'center' }}>
            <Typography.Text
              style={{
                fontSize: 16,
                lineHeight: 1,
                fontWeight: 700,
                color: colors.primary,
              }}
            >
              {`${numberWithDelimiter(events.length)} ${t('イベント')}`}
            </Typography.Text>
          </div>
          <Row gutter={[16, 32]} style={{ marginTop: xs ? 0 : 28 }}>
            {events.map((event) => {
              return (
                <Col key={event.id} xs={12} md={8}>
                  <EventCard event={event} style={{ width: '100%' }} />
                </Col>
              )
            })}
          </Row>
        </div>
      </div>
    </DefaultLayout>
  )
}

export default Page

const StyledArtWorkFilterFormWrapper = styled.div<{
  xs: boolean
  isOpenFilterFormForSp: boolean
}>`
${(props) =>
  props.xs
    ? `
  width:100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: white;
  z-index: ${zIndex.header + 1};
  overflow: scroll;
  ${!props.isOpenFilterFormForSp && 'display: none;'}
  `
    : `
  width: 225px;
  `}
}
`

const FixedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: white;
  padding: 16px;
  z-index: 5;
  width: 100vw;
`

Page.getHead = ({ topEvent }) => {
  return (
    <NextHead
      title="イベント一覧｜ArtX"
      type="website"
      thumbnailUrl={topEvent?.main_event_image_url}
      description="アートフェア、展示会、ワークショップなどのアートイベントの最新情報をお届けします。"
    />
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
}) => {
  const { data: events } = await api.get<Event[]>('/home/events?limit=10')

  return {
    props: {
      // NOTE: 画像を設定していないギャラリーがあるため、その場合は画像を設定している最初の作品を設定する。
      topEvent: events.find((v) => !!v.main_event_image_url) || null,
      ...(await serverSideTranslations(locale ?? 'ja', ['common'])),
    },
  }
}
